import React from 'react';
import type { VehicleGraphData, SbrDiagnosticEntity } from '@packages/domain/cvt';
import { createUseStyles } from 'react-jss';
import { CvtReportGraph } from './cvt-report-graph';
import { CvtReportTable } from './cvt-report-table';
import { DetailViewBody } from '../../components/detail-view/detail-view-body';
import { LoadingCircle, Typography } from '@packages/ui/shared';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles({
    panelCenterContainer: {
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'center',
        minHeight: '70vh',
        gap: 16,
    },
    panelStartContainer: {
        alignItems: 'start',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'center',
        minHeight: '70vh',
        gap: 16,
    },
    titleContainer: {
        marginBottom: 24,
    },
    flexBasis50: {
        flexBasis: '50%',
    },
    contentContainer: {
        maxHeight: '70vh',
        overflow: 'auto',
        padding: '24px 32px',
    },
    errorTextContainer: {
        display: 'flex',
        flexDirection: 'column',
        paddingBottom: 20,
        gap: 16,
    },
});

type CvtReportDetailsProps = {
    sbrData?: SbrDiagnosticEntity;
    graphData?: VehicleGraphData[];
    isLoading?: boolean;
};
const CvtReportDetails = ({ sbrData, graphData, isLoading = false }: CvtReportDetailsProps) => {
    const { t } = useTranslation();
    const classes = useStyles();

    const errorDetail = sbrData?.errorDetail;
    const errorDetailLines = errorDetail?.split('\n').filter(Boolean) ?? [];
    const hasErrorDetail = errorDetailLines.length > 0;

    const isNoResult = 'NoResult' === sbrData?.errorType;
    const containerPanelClass = isNoResult ? classes.panelStartContainer : classes.panelCenterContainer;

    if (isLoading) {
        return (
            <DetailViewBody className="p-0">
                <div className={classes.contentContainer}>
                    <div className={classes.panelCenterContainer}>
                        <LoadingCircle size={64} />
                    </div>
                </div>
            </DetailViewBody>
        );
    }

    if (!sbrData) {
        return (
            <DetailViewBody className="p-0">
                <div className={classes.contentContainer}>
                    <div className={classes.panelCenterContainer}>
                        <Typography>{t('cvt:reportDetails.noDataMessage')}</Typography>
                    </div>
                </div>
            </DetailViewBody>
        );
    }

    if (hasErrorDetail) {
        return (
            <DetailViewBody className="p-0">
                <div className={classes.contentContainer}>
                    <div className={containerPanelClass}>
                        {errorDetailLines.map((line, index) => (
                            <Typography key={index} variant="lead">
                                {line}
                            </Typography>
                        ))}
                    </div>
                </div>
            </DetailViewBody>
        );
    }

    return (
        <DetailViewBody className="p-0">
            <div className={classes.contentContainer}>
                <div className={classes.titleContainer}>
                    <Typography variant="h3">{t('cvt:reportDetails.title')}</Typography>
                </div>

                <CvtReportTable symptoms={sbrData?.symptoms} repairRecommendation={sbrData?.repairRecommendation} />
                <div className="mt-5">
                    <CvtReportGraph data={graphData ?? []} />
                </div>
            </div>
        </DetailViewBody>
    );
};

export default CvtReportDetails;
